/**
 * Display labels + tone mapping for compliance entities. Kept in one place so
 * the calendar, requirements, and task pages read consistently.
 */
import type {
  ComplianceAuthority,
  ComplianceCategory,
  RequirementSource,
  TaskStatus,
} from "@/lib/api/compliance";
import type { ComplianceFrequency, TaskUrgency } from "@argus/engine";

type Tone = "neutral" | "brand" | "clear" | "risk" | "breach" | "info" | "ai";

export const CATEGORY_LABEL: Record<ComplianceCategory, string> = {
  regulatory_filing: "Regulatory filing",
  regulatory_attestation: "Attestation",
  lp_report: "LP report",
  lp_contractual: "LP contractual",
  tax: "Tax",
  internal_policy: "Internal policy",
  other: "Other",
};

export const AUTHORITY_LABEL: Record<ComplianceAuthority, string> = {
  sec: "SEC",
  irs: "IRS",
  state: "State",
  cftc_nfa: "CFTC/NFA",
  eu: "EU/UK",
  finra: "FINRA",
  internal: "Internal",
  lp: "LP",
  other: "Other",
};

export const FREQUENCY_LABEL: Record<ComplianceFrequency, string> = {
  one_time: "One-time",
  monthly: "Monthly",
  quarterly: "Quarterly",
  semi_annual: "Semi-annual",
  annual: "Annual",
  biennial: "Biennial",
  on_event: "On event",
};

export const STATUS_LABEL: Record<TaskStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  blocked: "Blocked",
  submitted: "Submitted",
  completed: "Completed",
  waived: "Waived",
};

export const STATUS_TONE: Record<TaskStatus, Tone> = {
  open: "neutral",
  in_progress: "info",
  blocked: "risk",
  submitted: "brand",
  completed: "clear",
  waived: "neutral",
};

export const URGENCY_TONE: Record<TaskUrgency, Tone> = {
  overdue: "breach",
  due_soon: "risk",
  upcoming: "info",
  scheduled: "neutral",
  done: "clear",
};

export const URGENCY_LABEL: Record<TaskUrgency, string> = {
  overdue: "Overdue",
  due_soon: "Due soon",
  upcoming: "Upcoming",
  scheduled: "Scheduled",
  done: "Done",
};

export const SOURCE_LABEL: Record<RequirementSource, string> = {
  regulatory_catalog: "Regulatory",
  side_letter: "Side letter",
  manual: "Custom",
};

/** Ordered list of task statuses a user can move a task to from the UI. */
export const TASK_STATUS_FLOW: TaskStatus[] = [
  "open",
  "in_progress",
  "blocked",
  "submitted",
  "completed",
  "waived",
];
